import { useQueryParams, useClientPortalAPI } from "@certn/api";
import { Text, Stack, Link } from "@certn/cascadia";
import {
  BasicButton,
  Container,
  Layout,
  MediaQuery,
  Pagination,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { SkeletonGrid } from "@certn/cascadia-react/patterns/skeleton-grid/SkeletonGrid";
import { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { GroupsTable } from "./components/GroupsTable";
import { BillingProfileNameFilter } from "./components/filters/BillingProfileNameFilter";
import { PayerFilter } from "./components/filters/PayerFilter";
import { StatusFilter } from "./components/filters/StatusFilter";
import { TableDataCard } from "./components/mobile/TableDataCard";
import { toggleFavourite } from "./helpers";
import { AppFooterLinks } from "../../components/app-footer/AppFooterLinks";
import { AppHeader } from "../../components/app-header/AppHeader";
import { AppNavigation } from "../../components/app-navigation/AppNavigation";
import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useCanCreateGroups } from "../../components/permissions/Permissions";
import { useClientAccount } from "../../hooks/useClientAccount";
import { useBillingProfilesCompact } from "../../hooks/useBillingProfilesCompact";
export function GroupsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClientAccount();
  const { params, flatParams, setParam } = useQueryParams();
  const [billingProfileNameOptions, setBillingProfileNameOptions] = useState([
    { id: "", name: "" },
  ]);

  const {
    data: groups,
    isLoading,
    mutate,
  } = useClientPortalAPI(
    "/api/internal/client-portal/customer-accounts/customer-groups-list/",
    {
      page: flatParams.page,
      page_size: flatParams.page_size,
      ordering: flatParams.ordering,
      billing_profile_ids: flatParams.billing_profile_ids,
      is_active: flatParams.is_active,
      search: flatParams.search,
      payer: flatParams.payer,
    }
  );

  const { data: billingProfiles } = useBillingProfilesCompact(client?.id, {
    archived: "ACTIVE",
    ordering: "profile_name",
  });

  useEffect(() => {
    if (billingProfiles && billingProfiles.length > 0) {
      setBillingProfileNameOptions(
        billingProfiles.map((profile) => {
          return {
            id: profile.id || "",
            name: profile.profile_name || "",
          };
        })
      );
    }
  }, [billingProfiles]);

  return (
    <Layout
      layout="wide"
      header={<AppHeader />}
      navigation={<AppNavigation />}
      footer={<AppFooterLinks />}
    >
      <Container layout="full-width">
        <PageTitle
          isLoading={isLoading}
          actions={
            useCanCreateGroups() && (
              <BasicButton
                testID="create-group-button"
                onPress={() => {
                  navigate("/groups/create");
                }}
              >
                {t("New group")}
              </BasicButton>
            )
          }
        >
          {isLoading ? <Skeleton width="120px" /> : <Trans>Groups</Trans>}
        </PageTitle>
        <MediaQuery sm type="hide">
          <Stack orientation="horizontal" gap="space-2">
            <Text color="text-weak" isLoading={isLoading}>
              <Trans>
                Groups are a way to organize your cases into teams, projects or
                departments within your organization.
              </Trans>
            </Text>
            {isLoading ? (
              <Skeleton width="60px" />
            ) : (
              <Link
                target="_blank"
                href="https://portal.certn.co/hc/en-us/articles/27891960924563-Groups"
                textStyle="global/body-secondary"
                linkStyle="monochrome"
                testID="groups-learn-more-link"
              >
                <Trans>Learn more</Trans>
              </Link>
            )}
          </Stack>
        </MediaQuery>
        {isLoading ? (
          <SkeletonGrid rows={1} columns={2} />
        ) : (
          <FilterBar hasSearch testID="groups">
            <BillingProfileNameFilter
              billingProfileNameOptions={billingProfileNameOptions}
            />
            <StatusFilter />
            {client?.applicant_pay_enabled ? <PayerFilter /> : null}
          </FilterBar>
        )}
        <MediaQuery sm type="hide">
          {isLoading ? (
            <SkeletonGrid />
          ) : (
            <GroupsTable
              onSortChange={(sortDescriptor) =>
                setParam(
                  "ordering",
                  sortDescriptorToOrderingParam(sortDescriptor)
                )
              }
              sortDescriptor={orderingParamToSortDescriptor(
                flatParams["ordering"]
              )}
              results={groups?.results}
              isLoading={isLoading}
              mutate={mutate}
              onFavouriteClick={(
                id: string,
                isFavourite: boolean,
                t: TFunction,
                mutate: () => void
              ) => toggleFavourite(id, isFavourite, t, mutate)}
            />
          )}
        </MediaQuery>
        <MediaQuery sm type="show">
          <Container layout="full-width">
            {groups?.results &&
              groups?.results.map((group) => {
                return (
                  <TableDataCard
                    id={group.id}
                    key={group.id}
                    name={group.name}
                    userCount={group.user_count}
                    applicantFacingName={group.applicant_facing_name}
                    billingProfileName={group.billing_profile.profile_name}
                    isActive={group.is_active}
                    isFavourite={group.is_favourite}
                    mutate={mutate}
                    onFavouriteClick={() =>
                      toggleFavourite(group.id, group.is_favourite, t, mutate)
                    }
                  />
                );
              })}
          </Container>
        </MediaQuery>
        <Pagination
          isLoading={isLoading}
          setPage={(page) => setParam("page", page.toString())}
          setPageCount={(pageCount) =>
            setParam("page_size", pageCount.toString())
          }
          total={groups?.count ?? 0}
          page={params.page ? Number(params.page) : 1}
          pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
        />
      </Container>
    </Layout>
  );
}
